import {formatDate} from '@angular/common';
import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ActorId, IServerApi} from '@service-and-repairs/awpintegrationlib';
import {Subject} from 'rxjs';
import {DmsVehicle} from '../../../components/dms-search/dms-vehicle';
import {DmsService} from '../../../components/dms-search/dms.service';
import {OrderAndAppointmentSearchResult} from '../../../components/dms-search/order-and-appointment-search-result';
import {
  CaseSelectionOverlayComponent
} from '../../../components/service-case-bar/case/selection/case-selection-overlay.component';
import {
  VehicleSelectionOverlayComponent
} from '../../../components/vehicle/vehicle-selection-overlay/vehicle-selection-overlay.component';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {AnalyticsAction} from '../../analytics/enums/analytics-action';
import {VehicleSearchOrigin} from '../../analytics/enums/vehicle-search-origin';
import {AnalyticsService} from '../../analytics/services/analytics.service';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {VehicleDataLoader} from '../../vehicle/services/vehicle-data.loader';
import {VehicleLoader} from '../../vehicle/services/vehicle.loader';
import {ServiceCaseLoader} from '../services/service-case.loader';
import {ServiceCase} from './service-case';
import {ServiceCaseHolder} from './service-case-holder';
import {ServiceCaseUpdater} from './service-case-updater';

@Injectable({
  providedIn: 'root'
})
export class ServiceCaseManager {
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly dmsService: DmsService = inject(DmsService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly serviceCaseLoader: ServiceCaseLoader = inject(ServiceCaseLoader);
  private readonly serviceCaseUpdater: ServiceCaseUpdater = inject(ServiceCaseUpdater);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly userService: UserService = inject(UserService);
  private readonly vehicleLoader: VehicleLoader = inject(VehicleLoader);
  private readonly vehicleDataLoader: VehicleDataLoader = inject(VehicleDataLoader);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);

  private readonly awpLib: IServerApi;

  private caseSelectionOverlay: CaseSelectionOverlayComponent;

  caseLoadingStarted: Subject<void> = new Subject<void>();

  caseLoadingFinished: Subject<void> = new Subject<void>();

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;
    this.translate.onLangChange.subscribe(this.reloadAllServiceCases.bind(this));
    this.subscribeToRequestToOpenServiceCaseByVin();
    this.subscribeToServiceCasesLockedInBackend();
  }

  setSelectionOverlays(vehicleSelectionOverlay: VehicleSelectionOverlayComponent,
                       caseSelectionOverlay: CaseSelectionOverlayComponent): void {
    this.vehicleLoader.vehicleSelectionOverlay = vehicleSelectionOverlay;
    this.caseSelectionOverlay = caseSelectionOverlay;
  }

  // region create cases

  openNewServiceCaseWithoutVehicle(): void {
    this.createAndOpenServiceCase(
      ServiceCase.constructMinimalServiceCase(this.userService.getCurrentUser()),
      VehicleSearchOrigin.NO_VEHICLE
    );
  }

  openNewServiceCaseByTypeCode(typeCode: string, origin: VehicleSearchOrigin): void {
    const serviceCase: ServiceCase = ServiceCase.constructMinimalServiceCase(this.userService.getCurrentUser());
    serviceCase.setTypeCode(typeCode.toUpperCase());
    this.createAndOpenServiceCase(serviceCase, origin);
  }

  private openNewServiceCaseByVin(vin: string, origin: string): void {
    const serviceCase: ServiceCase = ServiceCase.constructMinimalServiceCase(this.userService.getCurrentUser());
    vin = vin.toUpperCase();
    serviceCase.setVinLong(vin.length === 17 ? vin : null);
    serviceCase.setVinShort(vin.slice(vin.length - 7));
    this.createAndOpenServiceCase(serviceCase, origin);
  }

  private openNewServiceCaseByDmsVehicle(vehicle: DmsVehicle, origin: string): void {
    const serviceCase: ServiceCase = ServiceCase.constructMinimalServiceCase(this.userService.getCurrentUser());
    serviceCase.setDmsOrderNumber(vehicle.orderNumber);
    serviceCase.updateCustomerName({
      firstName: vehicle.customerFirstName,
      lastName: vehicle.customerLastName,
      name: vehicle.customerName
    });
    serviceCase.setDmsCustomerNumber(vehicle.customerId);
    const vin: string = vehicle.vin.toUpperCase();
    serviceCase.setVinLong(vin.length === 17 ? vin : null);
    serviceCase.setVinShort(vin.slice(vin.length - 7));
    serviceCase.setLicensePlate(vehicle.licensePlate);
    this.createAndOpenServiceCase(serviceCase, origin);
  }

  private createAndOpenServiceCase(serviceCase: ServiceCase, origin: string): void {
    this.caseLoadingStarted.next();
    this.serviceCaseLoader.createServiceCase(serviceCase)
      .then((createdServiceCase: ServiceCase): void => {
        // Frontend timestamp can be manipulated. Therefore, we reset the default title of the service case
        // according to the actual timestamp set in the backend with respect to the user's locale.
        createdServiceCase.setTitle(formatDate(
          createdServiceCase.getCreatedAt(),
          'short',
          this.userService.getCurrentUser().getLocale()
        ));
        this.vehicleLoader.loadVehicleInServiceCase(createdServiceCase)
          .then((mustSaveServiceCase: boolean): void => {
            if (mustSaveServiceCase) {
              this.serviceCaseUpdater.saveServiceCaseMetadata(createdServiceCase, origin);
            }
            this.openServiceCase(createdServiceCase, true, origin);
            this.analyticsService.postServiceCaseTabEvent(AnalyticsAction.OPEN_IN_NEW_TAB, origin, createdServiceCase);
          });
      })
      .catch((error: Error): void => this.handleServiceCaseOpenError(error));
  }

  // endregion create service cases

  // region load service cases

  openExistingServiceCaseById(externalId: string, origin: string, skipUpdateLocalStorage?: boolean): Promise<void> {
    return new Promise<void>((resolve, reject): void => {
      this.caseLoadingStarted.next();
      this.serviceCaseLoader.getServiceCaseById(externalId)
        .then((serviceCase: ServiceCase): void => {
          this.vehicleLoader.loadVehicleInServiceCase(serviceCase)
            .then((mustSaveServiceCase: boolean): void => {
              if (mustSaveServiceCase) {
                this.serviceCaseUpdater.saveServiceCaseMetadata(serviceCase, origin);
              }
              this.openServiceCase(serviceCase, true, origin, skipUpdateLocalStorage);
              this.analyticsService.postServiceCaseTabEvent(AnalyticsAction.OPEN_IN_NEW_TAB, origin, serviceCase);
              resolve();
            });
        })
        .catch((error: Error): void => {
          this.handleServiceCaseOpenError(error);
          reject(error);
        });
    });
  }

  reloadServiceCaseById(externalId: string, setActive: boolean, skipUpdateLocalStorage?: boolean): Promise<void> {
    return new Promise(resolve => {
      this.caseLoadingStarted.next();
      this.serviceCaseLoader.getServiceCaseById(externalId)
        .then((serviceCase: ServiceCase): Promise<void> =>
          this.vehicleLoader.loadVehicleInServiceCase(serviceCase)
            .then((mustSaveServiceCase: boolean): void => {
              if (mustSaveServiceCase) {
                this.serviceCaseUpdater.saveServiceCaseMetadata(serviceCase, 'reload');
              }
              this.openServiceCase(serviceCase, setActive, 'reload', skipUpdateLocalStorage);
            }))
        .catch((error: Error): void => this.handleServiceCaseOpenError(error))
        .finally(resolve);
    });
  }

  selectAndOpenServiceCaseByVin(vin: string, origin: string): void {
    this.caseLoadingStarted.next();
    const user: UserData = this.userService.getCurrentUser();
    const checkForExistingCases: boolean = !user.getNewCaseAfterVehicleIdentification();
    if (checkForExistingCases) {
      Promise
        .all([
          this.serviceCaseLoader.getServiceCasesByVin(vin),
          this.dmsService.searchOrdersAndAppointmentsViaDmsInterface(vin)
        ])
        .then((results: [ServiceCase[], OrderAndAppointmentSearchResult[]]): void => {
          const serviceCases: ServiceCase[] = results[0];
          const dmsOrdersAndAppointments: OrderAndAppointmentSearchResult[] = this.filterDmsOrdersByServiceCases(
            results[1],
            serviceCases
          );
          if (serviceCases.length === 0 && dmsOrdersAndAppointments.length === 0) {
            this.openNewServiceCaseByVin(vin, origin);
          } else {
            this.offerExistingServiceCaseToOpen(serviceCases, dmsOrdersAndAppointments, vin, origin);
          }
        })
        .catch(error => this.handleServiceCaseOpenError(error));
    } else {
      this.openNewServiceCaseByVin(vin, origin);
    }
  }

  selectAndOpenServiceCaseByDmsVehicle(vehicle: DmsVehicle, origin: string): void {
    this.caseLoadingStarted.next();
    const user: UserData = this.userService.getCurrentUser();
    const checkForExistingCases: boolean = !user.getNewCaseAfterVehicleIdentification();
    if (checkForExistingCases) {
      this.serviceCaseLoader.getServiceCasesByVin(vehicle.vin)
        .then((serviceCases: ServiceCase[]): void => {
          serviceCases = this.filterServiceCasesByDmsOrder(serviceCases, vehicle.orderNumber);
          if (serviceCases.length === 0) {
            this.openNewServiceCaseByDmsVehicle(vehicle, origin);
          } else {
            this.offerExistingServiceCaseToOpenWithDmsVehicle(serviceCases, vehicle, origin);
          }
        }).catch(error => this.handleServiceCaseOpenError(error));
    } else {
      this.openNewServiceCaseByDmsVehicle(vehicle, origin);
    }
  }

  private offerExistingServiceCaseToOpen(serviceCases: ServiceCase[],
                                         dmsOrdersAndAppointments: OrderAndAppointmentSearchResult[],
                                         vin: string,
                                         origin: string): void {
    this.caseSelectionOverlay.show(serviceCases, dmsOrdersAndAppointments)
      .then((serviceCase: ServiceCase): void => {
        if (serviceCase?.isPersisted()) {
          this.openExistingServiceCaseById(serviceCase.getExternalId(), origin)
            .finally((): void => {
              // do nothing
            });
        } else if (serviceCase) {
          // User has selected pre-existing DMS order, see DmsOrderForVinSelectionComponent.
          // This creates a new, not yet persisted service case with rudimentary data.
          this.createAndOpenServiceCase(serviceCase, origin);
        } else {
          this.openNewServiceCaseByVin(vin, origin);
        }
      })
      .catch((): void => this.caseLoadingFinished.next());
  }

  private offerExistingServiceCaseToOpenWithDmsVehicle(serviceCases: ServiceCase[],
                                                       dmsVehicleSearchResult: DmsVehicle,
                                                       origin: string): void {
    this.caseSelectionOverlay.show(serviceCases, [])
      .then((serviceCase: ServiceCase): void => {
        if (serviceCase) {
          this.openExistingServiceCaseById(serviceCase.getExternalId(), origin)
            .finally((): void => {
              // do nothing
            });
        } else {
          this.openNewServiceCaseByDmsVehicle(dmsVehicleSearchResult, origin);
        }
      })
      .catch(() => this.caseLoadingFinished.next());
  }

  private filterServiceCasesByDmsOrder(serviceCases: ServiceCase[], orderNumber: string): ServiceCase[] {
    // filter for service cases that match selected dms order
    if (orderNumber) {
      serviceCases = serviceCases
        .filter((serviceCase: ServiceCase): boolean => serviceCase.getDmsOrderNumber() === orderNumber);
    }

    return serviceCases;
  }

  private filterDmsOrdersByServiceCases(ordersAndAppointments: OrderAndAppointmentSearchResult[],
                                        serviceCases: ServiceCase[]): OrderAndAppointmentSearchResult[] {
    // filter dms orders/appointments that do not match existing service cases
    return ordersAndAppointments.filter((order: OrderAndAppointmentSearchResult) => serviceCases
      .every((serviceCase: ServiceCase): boolean => serviceCase.getDmsOrderNumber() !== order.dmsId));
  }

  private subscribeToRequestToOpenServiceCaseByVin(): void {
    this.awpLib.subscribeToOpenServiceCaseByVin(
      (vin: string, origin: ActorId): void => this.selectAndOpenServiceCaseByVin(vin, origin)
    );
  }

  /**
   * Reloads a service case the backend rejected a write for because it is locked. The reload brings in the lock
   * state, which switches the UI to read-only.
   */
  private subscribeToServiceCasesLockedInBackend(): void {
    this.serviceCaseUpdater.serviceCaseLockedInBackend.subscribe((externalId: string): void => {
      const setActive: boolean = externalId === this.serviceCaseHolder.getActiveCase()?.getExternalId();
      this.reloadServiceCaseById(externalId, setActive)
        .finally((): void => {
          // do nothing
        });
    });
  }

  restoreNonActiveServiceCases(serviceCaseIds: string[]): void {
    console.log('[Webapp] [ServiceCaseManager] Restoring inactive service cases.');
    console.log('[Webapp] [ServiceCaseManager] Read ids from storage: '
      + this.serviceCaseHolder.getServiceCaseIdsFromLocalStorage().join(', '));
    Promise
      .all(serviceCaseIds.map((externalId: string): Promise<void> => new Promise<void>(resolve => {
        if (this.serviceCaseHolder.isServiceCaseOpen(externalId)) {
          // ignore already opened service cases
          resolve();
        } else {
          this.reloadServiceCaseById(externalId, false, true).then((): void => resolve());
        }
      })))
      .then((): void => {
        this.serviceCaseHolder.sortServiceCases((left: ServiceCase, right: ServiceCase): number =>
          serviceCaseIds.indexOf(left.getExternalId()) - serviceCaseIds.indexOf(right.getExternalId())
        );
      })
      .finally((): void => {
        this.serviceCaseHolder.updateOpenCasesInLocalStorage();
        console.log('[Webapp] [ServiceCaseManager] Done restoring inactive service cases: '
          + this.serviceCaseHolder.getServiceCaseIdsFromLocalStorage().join(', '));
      });
  }

  // endregion load service cases

  // region open service cases

  private openServiceCase(serviceCase: ServiceCase,
                          setActive: boolean,
                          origin: string,
                          skipUpdateLocalStorage?: boolean): void {
    this.serviceCaseHolder.openServiceCaseOrSetActive(serviceCase, setActive, skipUpdateLocalStorage);
    this.caseLoadingFinished.next();
    this.vehicleDataLoader.loadVehicleRelatedData(serviceCase)
      .then((mustSaveServiceCase: boolean): void => this.onServiceCaseUpdated(
        serviceCase,
        mustSaveServiceCase,
        origin
      ));
  }

  onServiceCaseUpdated(serviceCase: ServiceCase, mustSaveServiceCase: boolean, origin: string): void {
    if (mustSaveServiceCase) {
      this.serviceCaseUpdater.saveServiceCaseMetadata(serviceCase, origin);
    }
    if (serviceCase.getExternalId() === this.serviceCaseHolder.getActiveCase()?.getExternalId()) {
      this.serviceCaseHolder.onActiveCaseChanged();
    }
  }

  private handleServiceCaseOpenError(error: Error): void {
    console.warn(error.message);
    this.awpLib.notifyError(error.message);
    this.caseLoadingFinished.next();
  }

  // endregion open service cases

  // region update service cases

  reloadAllServiceCases(): void {
    this.serviceCaseHolder.serviceCases.forEach((serviceCase: ServiceCase): void => {
      const setActive: boolean =
        serviceCase.getExternalId() === this.serviceCaseHolder.getActiveCase()?.getExternalId();
      this.reloadServiceCaseById(serviceCase.getExternalId(), setActive)
        .finally((): void => {
          // do nothing
        });
    });
  }

  // endregion update service cases
}
